/**
 * CR-6347: Classification section of the reworked PO Lines filter panel
 * (customer view). Groups the part-classification filters — Commodity Group ->
 * Commodity Type (cascade preserved), Part Number and Description — per the
 * supplied diagram. Input types are unchanged from the previous flat panel.
 */
import type { LucideIcon } from 'lucide-react';
import { POLinesFilters, FilterOptions } from '../../types';
import { FilterSection, FIELD_CLS, LABEL_CLS } from '../fieldStyles';
import { useFilterVisibility } from '../../../../../../hooks/useFilterVisibility';

interface ClassificationSectionProps {
  filters: POLinesFilters;
  onFilterChange: (key: string, value: string) => void;
  commodityGroups: FilterOptions['commodity_groups'];
  commodityTypes: FilterOptions['commodity_types'];
  // Clears the now-invalid Commodity Type when the Group changes.
  onCommodityGroupChange: (value: string) => void;
  /** CR-6347: Optional icon for the section header */
  icon?: LucideIcon;
}

export function ClassificationSection({
  filters,
  onFilterChange,
  commodityGroups,
  commodityTypes,
  onCommodityGroupChange,
  icon,
}: ClassificationSectionProps) {
  // CR-4802: filters this tenant shows — one answer for every panel.
  const fv = useFilterVisibility();
  return (
    <FilterSection title="Classification" icon={icon}>
      {/* Commodity Group — CR-4802: the pair hides together when none are configured */}
      {fv.commodity && (<>
      <div>
        <label className={LABEL_CLS}>Commodity Group</label>
        <select
          value={filters.commodityGroup}
          onChange={(e) => onCommodityGroupChange(e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All Commodity Groups</option>
          {commodityGroups.map((cg) => (
            <option key={cg.id} value={cg.id}>{cg.name}</option>
          ))}
        </select>
      </div>

      {/* Commodity Type (cascaded by Commodity Group) */}
      <div>
        <label className={LABEL_CLS}>Commodity Type</label>
        <select
          value={filters.commodityType}
          onChange={(e) => onFilterChange('commodityType', e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All Commodity Types</option>
          {commodityTypes
            .filter(
              (ct) =>
                !filters.commodityGroup ||
                ct.commodity_group_id === parseInt(filters.commodityGroup, 10),
            )
            .map((ct) => (
              <option key={ct.id} value={ct.id}>{ct.name}</option>
            ))}
        </select>
      </div>
      </>)}

      {/* Part Number */}
      <div>
        <label className={LABEL_CLS}>Part Number</label>
        <input
          type="text"
          placeholder="Part number..."
          value={filters.partNumber}
          onChange={(e) => onFilterChange('partNumber', e.target.value)}
          className={FIELD_CLS}
        />
      </div>

      {/* Part Description — the PART MASTER's name for the part (CR-7308) */}
      <div>
        <label className={LABEL_CLS}>Part Description</label>
        <input
          type="text"
          placeholder="Part description..."
          value={filters.partDescription}
          onChange={(e) => onFilterChange('partDescription', e.target.value)}
          className={FIELD_CLS}
        />
      </div>

      {/* Order Description — the line's own text. Its column is still on
          this grid (Aaron, 19 Sep), so it keeps its own box. */}
      <div>
        <label className={LABEL_CLS}>Order Description</label>
        <input
          type="text"
          placeholder="Order description..."
          value={filters.description}
          onChange={(e) => onFilterChange('description', e.target.value)}
          className={FIELD_CLS}
        />
      </div>
    </FilterSection>
  );
}
